/**
 * Generate avatar initials from a full name
 * Takes the first letter of the first 2 words
 * @param {string} fullName - The full name of the user
 * @returns {string} The initials with space (e.g., "A B" for "Ahmad Baker")
 */
export const getAvatarInitials = (fullName) => {
  if (!fullName) return "";

  const words = fullName.trim().split(/\s+/);

  if (words.length === 0) return "";
  if (words.length === 1) return words[0].charAt(0).toUpperCase();

  // Take first letter from first 2 words with space between
  return (words[0].charAt(0) + " " + words[1].charAt(0)).toUpperCase();
};

/**
 * Generate a consistent color based on a string (name)
 * Uses a hash function to generate a color from the name
 * @param {string} name - The name to generate color from
 * @returns {string} A hex color code
 */
export const getAvatarColor = (name) => {
  if (!name) return "#1976d2"; // Default primary color

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }

  // Generate color with good contrast and saturation
  const hue = Math.abs(hash) % 360;
  const saturation = 65 + (Math.abs(hash) % 20); // 65-85%
  const lightness = 45 + (Math.abs(hash) % 15); // 45-60%

  return hslToHex(hue, saturation, lightness);
};

/**
 * Convert HSL to Hex color
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color code
 */
const hslToHex = (h, s, l) => {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};
